import {
  RIDE_METADATA_EDITABLE_FIELDS,
  type IRideDto,
  type IRideMetadata,
  type TRideMetadataEditableField,
} from "@ryde/shared";

const assignEditableMetadataField = <K extends TRideMetadataEditableField>({
  metadataComputed,
  fieldName,
  value,
}: {
  metadataComputed: IRideMetadata;
  fieldName: K;
  value: IRideMetadata[K];
}): void => {
  metadataComputed[fieldName] = value;
};

const dirtyMetadataMapFromForm = (
  dirtyMetadataFields: object | undefined,
): Record<string, unknown> => {
  if (!dirtyMetadataFields) {
    return {};
  }

  const dirtyMetadataMap: Record<string, unknown> = {};
  for (const [fieldName, dirtyFlag] of Object.entries(dirtyMetadataFields)) {
    dirtyMetadataMap[fieldName] = dirtyFlag;
  }
  return dirtyMetadataMap;
};

const toEditableRideMetadataField = (
  fieldName: string,
): TRideMetadataEditableField | undefined => {
  for (const editableField of RIDE_METADATA_EDITABLE_FIELDS) {
    if (editableField === fieldName) {
      return editableField;
    }
  }

  return undefined;
};

/**
 * Same rule as ride-form save: copy only dirty, form-editable metadata keys.
 * Stations, route and other DTO-only keys stay on the target object.
 */
export const applyDirtyEditableFormMetadata = ({
  formValuesSnapshot,
  dirtyMetadataFields,
  metadataComputed,
}: {
  formValuesSnapshot: Partial<IRideDto> | undefined;
  dirtyMetadataFields: object | undefined;
  metadataComputed: IRideMetadata;
}): void => {
  if (!formValuesSnapshot) {
    return;
  }

  const formHandledMetadata = dirtyMetadataMapFromForm(dirtyMetadataFields);

  for (const dirtyMetadataFieldName of Object.keys(formHandledMetadata)) {
    const editableFieldName = toEditableRideMetadataField(
      dirtyMetadataFieldName,
    );
    if (!editableFieldName) {
      continue;
    }

    if (formHandledMetadata[dirtyMetadataFieldName] === undefined) {
      continue;
    }

    assignEditableMetadataField({
      metadataComputed,
      fieldName: editableFieldName,
      value: formValuesSnapshot.metadata?.[editableFieldName],
    });
  }
};
